import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loader } from 'graphql.macro';
import { useQuery, useMutation } from 'react-apollo-hooks';
import { withRouter, Link } from 'react-router-dom';
import debounce from 'debounce';
import cn from 'classnames';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';

import {
  Hint,
  WithTitle,
  Button,
  Input,
  TimeInput,
  DateInput,
  Breadcrumbs,
  RadioLine,
  Table,
  useNotifications,
} from '@loyalup/ui';

import Filters from 'components/Filters';
import Phone from 'components/Phone';
import PhoneForm from 'components/PhoneForm';
import { getClientFields } from 'components/ClientFields';

import { ProfileContext, suppressError } from '@loyalup/core';
import store from 'utils/store';
import updateProfileInCache from 'utils/updateProfileInCache';
import onlyData from 'utils/onlyData';
import estimateSmsForText from 'utils/sms';

import { ReactComponent as SearchIcon } from 'icons/search.svg';
import { ReactComponent as QuitIcon } from 'icons/go-back.svg';

import s from './NewCampaign.module.css';

const COMPANY_QUERY = loader('../../gql/queries/Company.gql');
const CLIENTS_QUERY = loader('../../gql/queries/Clients.gql');
const CLIENTS_COUNTER_QUERY = loader('../../gql/queries/ClientsCounter.gql');
const CREATE_CAMPAIGN_MUTATION = loader('../../gql/mutations/CreateCampaign.gql');

const getSelection = ({ selection }) => ({
  ...onlyData(selection),
  conds: selection?.conds?.map(onlyData),
  all: selection.include === 'all',
  include: selection.include === 'all' ? [] : selection.include,
});

const getDateTime = ({ date, time }) => {
  const [hours, minutes] = time.split(':');
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), parseInt(hours), parseInt(minutes));
};

const PHONE_HINT_MESSAGE = {
  PUSH:
    'Рекомендуем заинтересовать клиента первыми 55 символами, вся остальная информация будет отображаться на обратной стороне карты при переходе по Push-уведомлению.',
  SMS:
    'Рекомендуем заинтересовать клиента первыми 55 символами, вся остальная информация будет отображаться после открытия SMS сообщения.',
  PUSHSMS:
    'Рекомендуем заинтересовать клиента первыми 55 символами, вся остальная информация будет отображаться на обратной стороне карты при переходе по Push-уведомлению или после открытия SMS сообщения.',
};

const lowSmsBalanceText = (t, count) =>
  t(
    'Ваш баланс SMS: {{count, number}}\n\nБаланса не хватает, чтобы отправить SMS всем выбранным клиентам.\n\nВы можете уменьшить количество SMS в рассылке, сделав текст короче, выбрав меньше клиентов или пополнив баланс SMS.',
    { count },
  );

const CreateCampaign = ({ match, history }) => {
  const [t] = useTranslation();
  const [sendNotification] = useNotifications();
  const preset = match.params && match.params.id && store.read(match.params.id);

  const [page, setPage] = useState(preset ? 2 : 1);

  const [campaign, setCampaign] = useState({
    selection: preset || { include: 'all', exclude: [], text: null, filterId: null },
    text: '',
    filter: null,
    type: 'PUSHSMS',
    date: new Date(),
    time: `${new Date().getHours() + 1}:00`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    creating: false,
  });

  // for table settings
  const { clientsTableSettings } = useContext(ProfileContext);
  const { orderBy, orderDirection } = clientsTableSettings;

  const columns = getClientFields({ asList: true, filter: clientsTableSettings.visibleColumns });
  const [tableConfig, setTableConfig] = useState({ offset: 0, limit: 10, orderBy, orderDirection });
  const [timePlanning, setTimePlanning] = useState(false);

  const clientsLoadVars = { filterId: campaign.selection.filterId, text: campaign.selection.text, ...tableConfig };

  const { loading, data, fetchMore } = useQuery(CLIENTS_QUERY, {
    suspend: false,
    variables: clientsLoadVars,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { data: companyData } = useQuery(COMPANY_QUERY, {
    suspend: false,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const loadClients = async () => {
    try {
      await fetchMore({
        variables: clientsLoadVars,
        updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult,
      });
    } catch (e) {
      suppressError(e, 'Invariant Violation: 15');
    }
  };

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableConfig, campaign.selection.filterId, campaign.selection.text]);

  const { data: counterData } = useQuery(CLIENTS_COUNTER_QUERY, {
    suspend: false,
    variables: { selection: getSelection(campaign) },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const clientsSelected = campaign.selection.include.length;
  const totalAvailableSms = companyData?.company.availableSms;
  const smsCampaignLength = estimateSmsForText(campaign.text);

  const outreachCounters = {
    total: counterData?.clients?.total || 0,
    cards: counterData?.clients?.totalCards || 0,
    phones: counterData?.clients?.totalPhones || 0,
    active: (counterData?.clients?.totalCards || 0) + (counterData?.clients?.onlyPhones || 0),
    push: (counterData?.clients?.totalCards || 0) * (campaign.text.length ? 1 : 0),
    sms: (counterData?.clients?.onlyPhones || 0) * smsCampaignLength,
    onlySms: (counterData?.clients?.totalPhones || 0) * smsCampaignLength,
  };

  const isDisabledForLowSMSBalance =
    (campaign.type === 'PUSHSMS' && outreachCounters.sms > totalAvailableSms) ||
    (campaign.type === 'SMS' && outreachCounters.onlySms > totalAvailableSms);

  const allowSend =
    campaign.text.length > 0 &&
    (campaign.type === 'PUSH' ? outreachCounters.push > 0 : outreachCounters.active > 0) &&
    !isDisabledForLowSMSBalance;

  const [createCampaignMutation] = useMutation(CREATE_CAMPAIGN_MUTATION, {
    update: cache => {
      updateProfileInCache(cache, ({ campaignsCount }) => ({
        campaignsCount: campaignsCount + 1,
      }));
    },
  });

  const createCampaign = async (campaign, schedule) => {
    setCampaign({ ...campaign, creating: true });
    const variables = {
      text: campaign.text,
      type: campaign.type,
      selection: getSelection(campaign),
      timezone: campaign.timezone,
      datetime: schedule && getDateTime(campaign),
    };
    await createCampaignMutation({ variables });

    sendNotification({ text: t(schedule ? 'Запланирована новая рассылка' : 'Запущена новая рассылка') });
    history.push(`/campaigns/list`);
  };

  //hack for pushing user to first page of the wizard if he clicks back in browser
  useEffect(
    () =>
      history.block((_, action) => {
        if (action === 'POP' && page === 2) {
          setPage(1);
          return 'PREVENT_BACK';
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page],
  );

  return (
    <WithTitle title={t('Новая рассылка')}>
      <div className={s.header}>
        <h1>
          <Link to="/campaigns/list" className={s.back}>
            <QuitIcon />
          </Link>
          {t('Рассылки')}
        </h1>
        <Breadcrumbs
          withArrows
          className={s.breadcrumbs}
          items={[
            {
              label: t('1. Получатели'),
              current: page === 1,
              disabled: false,
              onSelect: () => !campaign.creating && setPage(1),
            },
            {
              label: t('2. Сообщение и Отправка'),
              current: page === 2,
              disabled: !clientsSelected,
              onSelect: () => setPage(2),
            },
          ]}
        />
      </div>
      {page === 1 && (
        <>
          <div className={s.toolbar}>
            <Filters
              table="CLIENTS"
              theme="toolbar-white"
              className={s.toolbarItem}
              stretch
              fixedWidth={220}
              activeFilter={campaign.filter}
              onSelect={filter =>
                setCampaign({
                  ...campaign,
                  filter,
                  selection: { ...campaign.selection, filterId: filter && filter.id, conds: filter?.conds || [] },
                })
              }
              editable={false}
              disabled={!!campaign.selection.text}
            />
            <div className={s.spacer} />
            <div className={s.toolbarItem}>
              <Input
                theme="toolbar-white"
                expandWidth={300}
                leftIcon={<SearchIcon />}
                placeholder={t('Поиск')}
                onChange={debounce(
                  text => setCampaign({ ...campaign, selection: { ...campaign.selection, text } }),
                  300,
                )}
                disabled={!!campaign.filter}
              />
            </div>
          </div>
          <Table
            selectable
            selection={campaign.selection}
            total={data && data.clients && data.clients.total}
            tableConfig={tableConfig}
            columns={columns}
            rows={(data && data.clients && data.clients.clients) || []}
            loading={loading}
            highlightText={tableConfig.text}
            onSelectionChange={selection => {
              if (preset) store.update(match.params.id, selection);
              setCampaign({ ...campaign, selection: { ...campaign.selection, ...selection } });
            }}
            onChange={setTableConfig}
            renderActionComponent={() => (
              <div>
                <Button onClick={() => setPage(2)} fixedWidth={210} center disabled={!clientsSelected}>
                  {t('Далее')}
                </Button>
              </div>
            )}
          />
        </>
      )}
      {page === 2 && (
        <CSSTransitionGroup
          component="main"
          className={s.settingsContainer}
          transitionName={{ appear: s.appear, appearActive: s.appearActive }}
          transitionAppear={true}
          transitionAppearTimeout={0}
          transitionEnterTimeout={0}
          transitionLeaveTimeout={0}
        >
          <PhoneForm
            renderPhone={() => (
              <Phone
                notificationTitle={campaign.type === 'SMS' ? null : companyData?.company.name}
                notificationType={campaign.type === 'PUSHSMS' ? 'PUSH' : campaign.type}
                notificationText={campaign.text}
                hint={t(PHONE_HINT_MESSAGE[campaign.type])}
                iconSrc={
                  companyData?.company.currentPassDesign?.iconImage.url ||
                  companyData?.company.defaultPassDesign?.iconImage.url
                }
              />
            )}
          >
            {() => (
              <div className={s.messageSettings}>
                <div className={s.settingsSection}>
                  <h4>{t('Тип отправки')}</h4>
                  <div className={s.columnContainer}>
                    <RadioLine
                      selected={campaign.type}
                      items={[
                        {
                          onClick: () => setCampaign({ ...campaign, type: 'PUSHSMS' }),
                          label: t('Push и SMS'),
                          value: 'PUSHSMS',
                        },
                        {
                          onClick: () => setCampaign({ ...campaign, type: 'PUSH' }),
                          label: t('Только Push'),
                          value: 'PUSH',
                        },
                        {
                          onClick: () => setCampaign({ ...campaign, type: 'SMS' }),
                          label: t('Только SMS'),
                          value: 'SMS',
                        },
                      ]}
                    />
                    <Hint
                      position="down"
                      formatting
                      text={t(
                        'Push и SMS\nПослать пуш уведомление клиентам у которых электронная карта активна. Клиентам у которых карта не активна, но указан телефон - послать смс.\n\nТолько Push\nПослать пуш уведомление только клиентам с активной картой.\n\nТолько SMS\nПослать смс сообщение всем клиентам у которых указан телефон.',
                      )}
                    />
                  </div>
                </div>
                <div className={s.settingsSection}>
                  <h4 className={s.columnContainer}>
                    <div>{t('Текст уведомления')}</div>
                    <div className={s.spacer} />
                    <div>
                      {campaign.type !== 'PUSH' &&
                        t('SMS на сообщение: {{count, number}}', { count: smsCampaignLength })}
                    </div>
                  </h4>
                  <Input
                    theme="toolbar-white"
                    disabled={campaign.creating}
                    type="textarea"
                    placeholder={t('Введите текст сообщения')}
                    onChange={text => setCampaign({ ...campaign, text })}
                    value={campaign.text}
                    autoFocus
                    maxLength={500}
                    rows={6}
                  />
                </div>
                <div className={s.settingsSection}>
                  <h4>{t('Охват аудитории')}</h4>
                  <div className={s.columnContainer}>
                    <div className={s.outreach}>
                      <div className={s.stat}>
                        <div className={s.statLabel}>{t('Выбрано клиентов')}</div>
                        <div className={s.statValue}>{t('{{count, number}}', { count: outreachCounters.total })}</div>
                      </div>
                      <div className={s.stat}>
                        <div className={s.statLabel}>{t('Активных клиентов')}</div>
                        <div className={s.statValue}>
                          {t('{{count, number}}', {
                            count:
                              campaign.type === 'PUSH'
                                ? outreachCounters.cards
                                : campaign.type === 'PUSHSMS'
                                ? outreachCounters.active
                                : outreachCounters.phones,
                          })}
                        </div>
                      </div>
                      <div className={s.stat}>
                        <div className={s.statLabel}>{t('Push уведомлений')}</div>
                        <div className={s.statValue}>
                          {t('{{count, number}}', { count: campaign.type === 'SMS' ? 0 : outreachCounters.push })}
                        </div>
                      </div>
                      <div className={cn(s.stat, isDisabledForLowSMSBalance && s.statRed)}>
                        <div className={s.statLabel}>{t('SMS уведомлений')}</div>
                        <div className={s.statValue}>
                          {t('{{count, number}}', {
                            count:
                              campaign.type === 'PUSH'
                                ? 0
                                : campaign.type === 'SMS'
                                ? outreachCounters.onlySms
                                : outreachCounters.sms,
                          })}
                        </div>
                      </div>
                    </div>
                    <Hint
                      formatting
                      text={t(
                        'Активные клиенты - количество клиентов, которые смогут получить уведомление выбранным выше способом.\n\nПри отправке уведомления через SMS, длинный текст считается сотовым оператором, как несколько SMS сообщений. В этом случае общее количество SMS уведомлений к отправке будет кратно больше количества клиентов с телефонами.',
                      )}
                    />
                  </div>
                </div>
                {!timePlanning && (
                  <div className={s.settingsSection}>
                    <div className={s.button}>
                      <Button
                        disabled={!allowSend}
                        loading={campaign.creating}
                        center
                        fixedWidth={200}
                        countDown={3}
                        countDownMessage={s => t('Отправится через {{sec}} сек', { sec: s })}
                        onClick={() => createCampaign(campaign)}
                      >
                        {isDisabledForLowSMSBalance ? t('Недостаточный баланс') : t('Отправить сейчас')}
                      </Button>
                      {!isDisabledForLowSMSBalance && (
                        <Button
                          disabled={!allowSend}
                          loading={campaign.creating}
                          center
                          fixedWidth={200}
                          onClick={() => setTimePlanning(true)}
                        >
                          {t('Запланировать')}
                        </Button>
                      )}
                      {isDisabledForLowSMSBalance && <Hint formatting text={lowSmsBalanceText(t, totalAvailableSms)} />}
                    </div>
                  </div>
                )}

                {timePlanning && (
                  <div className={s.settingsSection}>
                    <h4>{t('Время отправки')}</h4>
                    <div className={s.columnContainer}>
                      <DateInput
                        theme="toolbar-white"
                        disabled={campaign.creating}
                        value={campaign.date}
                        onChange={date => setCampaign({ ...campaign, date })}
                      />
                      &nbsp;&nbsp;
                      <TimeInput
                        stretch
                        disabled={campaign.creating}
                        icon="time"
                        theme="toolbar-white"
                        value={campaign.time}
                        maxHeight={200}
                        fixedWidth={100}
                        onChange={time => setCampaign({ ...campaign, time })}
                      />
                      &nbsp;&nbsp;
                      <div>{campaign.timezone}</div>
                    </div>
                    <div className={s.button}>
                      <Button
                        disabled={!allowSend || getDateTime(campaign) < new Date()}
                        loading={campaign.creating}
                        center
                        fixedWidth={200}
                        onClick={() => createCampaign(campaign, true)}
                      >
                        {isDisabledForLowSMSBalance ? t('Недостаточный баланс') : t('Запланировать')}
                      </Button>
                      {!campaign.creating && (
                        <Button theme="black" onClick={() => setTimePlanning(false)}>
                          {t('Отмена')}
                        </Button>
                      )}
                      {isDisabledForLowSMSBalance && <Hint text={lowSmsBalanceText(t, totalAvailableSms)} />}
                    </div>
                  </div>
                )}
              </div>
            )}
          </PhoneForm>
        </CSSTransitionGroup>
      )}
    </WithTitle>
  );
};

export default withRouter(CreateCampaign);
